import React, { useState } from "react";
import Uppy from '@uppy/core';
import AwsS3 from "@uppy/aws-s3";
import { Button, Form } from "react-bootstrap";
import _ from 'lodash';
import uuidv4 from "uuid/v4";
import ToggleButton from 'react-toggle-button';
import imgCamelLogo from '../../assets/images/main-logo-white.png';
import ImageUploader from "../../components/settings/ImageUploader";
import CCColorPicker from "../../components/common/CCColorPicker";
import LoaderButton from "../../components/LoaderButton";
import { BRANDING } from "../../constants/Branding";
import "./CreateBrandingForm.scss";

const CC_COMPANION_URL = process.env.REACT_APP_COMPANION_URL; // Transloadit.COMPANION;

/**
 * One time init of Uppy. This is shared across all ImageUploaders
 * @returns {Uppy}
 */
const initUppy = () => {
  const uppy = new Uppy({
    id: "createBrandingUppy",
    debug: true,
    autoProceed: false,
    allowMultipleUploadBatches: false,
    restrictions: {
      allowedFileTypes: ["image/*"],
    },
    onBeforeUpload: (files) => {
      const updatedFiles = {};
      for (const [fileID, file] of Object.entries(files)) {
        if (file.meta.s3Key) {
          file.meta.key = file.meta.s3Key; 
          file.meta.name = file.meta.s3Key; 
        }
        updatedFiles[fileID] = file; 
      }
  
      return updatedFiles; 
    },
  });

  uppy.use(AwsS3, {
    limit: 3,
    timeout: 60000,
    companionUrl: process.env.REACT_APP_COMPANION_URL,
    companionHeaders: {
      allowedMetaFields: true
    }
  });

  return uppy;
}

// const handleBeforeFileAdded = (currentFile, files) => {
//   console.log({ currentName: currentFile.name, props, info: 'Before file added' });
//   `${props.user.tenantId}/branding/${brandingId}/${file.name}`, // file name
// }

const CreateBrandingForm = props => {
  const [brandingName, setBrandingName] = useState('');
  const [brandingId, setBrandingId] = useState(uuidv4());
  const [color, setColor] = useState(_.get(props.settings, `${BRANDING.SETTING}.${BRANDING.COLOR}`, '#ffffff'));
  const [isDefault, setIsDefault] = useState(false);
  const [uppyData, setUppyData] = useState({
    logo: null,
    siteImage: null,
    favicon: null,
  });
  const [uppyInstance] = useState(() => initUppy());
  const [submitting, setSubmitting] = useState(false)

  React.useEffect(() => {
    if (props.item) {
      setBrandingName(props.item.name);
      setIsDefault(props.item.default || false);

      if (props.item.color) {
        setColor(props.item.color);
      }
    }
  }, []);

  React.useEffect(() => {
    // Cleanup on unmount
    return () => {
      uppyInstance.close();
    };
  }, [uppyInstance]);

  React.useEffect(() => {
    // console.log({ brandingId, uppyInstance, info: 'setting options' });
    if (!brandingId) return;
    
    uppyInstance.setOptions({
      onBeforeFileAdded: (currentFile, files) => {
        // console.log({ brandingId, currentFile, info: 'Before file added' });
        const newName = `${props.user.tenantId}/branding/${brandingId}/${currentFile.name}`;
        const updatedFile = { ...currentFile };
        updatedFile.meta = updatedFile.meta ?? {};
        updatedFile.meta.s3Key = newName;
        updatedFile.meta.tenantId = props.user.tenantId;
        updatedFile.meta.brandingId = brandingId;
        // console.log({ updatedFile, info: 'Before file added' });
        
        return updatedFile;
      }
    });
  }, [brandingId, uppyInstance, props.user.tenantId]);


  const onSubmit = async (e) => {
    e.preventDefault();
    // console.log({ files: uppyInstance.getFiles(), info:'Files currently in Uppy' });
    setSubmitting(true)
    uppyInstance.upload()

    uppyInstance.on('complete', (result) => {
      // console.log({ result, submit:props.onSubmit, info: 'Uppy complete' });
      if (result.successful) {
        const logoResponse = result.successful.find(res => res.source === `dragdrop-${BRANDING.LOGO}`);
        const faviconResponse = result.successful.find(res => res.source === `dragdrop-${BRANDING.FAVICON}`);
        const siteImageResponse = result.successful.find(res => res.source === `dragdrop-${BRANDING.SITE_IMAGE}`);
        
        props.onSubmit({
          name: brandingName,
          color: color,
          isDefault: isDefault,
          logo: logoResponse ? logoResponse.uploadURL : null,
          favicon: faviconResponse ? faviconResponse.uploadURL : null,
          siteImage: siteImageResponse ? siteImageResponse.uploadURL : null,
        })
      }

      setSubmitting(false)
      props.onClose();
    })
    
    uppyInstance.on('error', (error) => {
      console.error('Error with upload:', error);
      setSubmitting(false);
    });
  }


  const validation = () => {
    if (brandingName === '') return true;

    return false;
  }

  // const setFileBlob = (name, file) => {
  //   uppyInstance.addFile({
  //     name: `${props.user.tenantId}/branding/${brandingId}/${file.name}`, // file name
  //     type: file.type, // file type
  //     data: file.data, // file blob
  //     meta: {
  //       // optional, store the directory path of a file so Uppy can tell identical files in different directories apart.
  //       // relativePath: webkitFileSystemEntry.relativePath,
  //       sourceType: name
  //     },
  //     source: 'Local', // optional, determines the source of the file, for example, Instagram.
  //     isRemote: false, // optional, set to true if actual file is not in the browser, but on some remote server, for example,
  //   })
  // }

  return (
    <Form className="create-branding-form">
      <div className="form-header">
        <img src={imgCamelLogo} alt="logo" />
        <button className="close-button" onClick={props.onClose}>×</button>
      </div>
      <div className="main-content">
      <div className="title">{props.isEditing ? 'Edit' : 'Add'} Branding</div>
        <div className="create-container">
          <div className="edit-box">
            <div className="title-description">
              <span>Define a branding option</span>
            </div>
            <Form.Group controlId="formMessage">
              <Form.Control
                type="text"
                placeholder="Name"
                onChange={e => setBrandingName(e.target.value)} value={brandingName}
              />
            </Form.Group>
            <div className="branding-settings-box">
              <div className="branding-settings-row">
                <div className="branding-component-container">
                  <div className="brand-settings-item">
                    <div className="title">Logo</div>
                    <div className="logo-picker">
                      <ImageUploader
                        id="logoUploader"
                        uppy={uppyInstance}
                        user={props.user}
                        type={BRANDING.LOGO}
                        typeId={brandingId}
                        tenantId={props.user.tenantId}
                        imageUrl={_.get(props.item, `${BRANDING.LOGO}`, null)}
                        autoProceed={false}
                        // setFileBlob={file => setFileBlob('logo', file)}
                        brandingName={brandingName}
                      />
                    </div>
                  </div>
                  <div className="brand-settings-item">
                    <div className="title">Favicon</div>
                    <div className="logo-picker">
                      <ImageUploader
                        id="faviconUploader"
                        uppy={uppyInstance}
                        user={props.user}
                        type={BRANDING.FAVICON}
                        typeId={brandingId}
                        tenantId={props.user.tenantId}
                        imageUrl={_.get(props.item, `${BRANDING.FAVICON}`, null)}
                        autoProceed={false}
                        // setFileBlob={file => setFileBlob('favicon', file)}
                        brandingName={brandingName}
                      />
                    </div>
                  </div>
                </div>
                <div className="branding-component-description">
                  The main logo is used to brand your page. The favicon is the small logo in browser tabs. Recommended logo size is 290x60px. Favicon size is 32x32px.
                </div>
              </div>

              <div className="branding-settings-row">
                <div className="branding-component-container">
                  <div className="brand-settings-item">
                    <div className="title">Accent color</div>
                    <CCColorPicker
                      value={color}
                      onChange={(hexColor) => setColor(hexColor)}
                    />
                  </div>
                </div>
                <div className="branding-component-description">
                  The main brain color used to tint the background of the share pages.
                </div>
              </div>

              <div className="branding-settings-row">
                <div className="branding-component-container">
                  <div className="brand-settings-item">
                    <div className="title">Share Thumbnail</div>
                    <div className="logo-picker">
                      <ImageUploader
                        id="siteImageUploader"
                        uppy={uppyInstance}
                        user={props.user}
                        type={BRANDING.SITE_IMAGE}
                        typeId={brandingId}
                        tenantId={props.user.tenantId}
                        imageUrl={_.get(props.item, `${BRANDING.SITE_IMAGE}`, null)}
                        autoProceed={false}
                        onUploadSuccess={props.fetchSettings}
                        // setFileBlob={file => setFileBlob('siteImage', file)}
                        brandingName={brandingName}
                      />
                    </div>
                  </div>
                </div>
                <div className="branding-component-description">
                  The social share image shown when the link is unfurled. Recommended image size is 1200x630px.
                </div>
              </div>
            </div>

            <div className="toggle-wrapper">
              <ToggleButton
                inactiveLabel={'NO'}
                activeLabel={'YES'}
                value={isDefault}
                thumbStyle={{ borderRadius: 2 }}
                trackStyle={{ borderRadius: 2 }}
                colors={{
                  active: {
                    base: 'rgb(76,210,126)',
                  } 
                }}
                onToggle={(value) => {
                  setIsDefault(!value)
                }}
              />
              <div className="toggle-label">
               Make default branding?
              </div>
            </div>

            <div className="actions-box">
            <Button
                onClick={props.onClose}
                variant="outline-danger"
                className="mr-2"
                size="sm"
              >
                Cancel
              </Button>
              <LoaderButton
                disabled={validation() || submitting}
                onClick={onSubmit}
                variant="outline-danger"
                className="mr-2"
                size="sm"
                isLoading={submitting}
                text={props.isEditing ? 'Edit' : 'Create'}
                loadingText=""
              />
              
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
}

export default CreateBrandingForm;
